/**
 * Calcoli della Panoramica, della presenza e dei segnali del menu (08 sez. 4 e 6, 15 sez. 1 e 3.1). Modulo puro.
 * Tutto deriva da commerce_stores (stato e sync_state): quando il problema sparisce, sparisce anche
 * l'evidenza (15 sez. 1 regola 7). Nessun numero per lavori in corso o errori nel menu.
 */
import { parseSyncState, storeProblems } from './syncState';
import { formatCountIt, relativeTimeIt } from './timeFormat';
import { PROVIDER_LABEL, reauthReason, storeDisplayName } from './uiLabels';
import type { CommerceProvider, StoreLabel, StoreStatus } from './types';

export interface CommercePresence {
  /** Almeno un negozio non scollegato (anche in analisi). */
  hasCommerce: boolean;
  /** WooCommerce ancora sul sistema vecchio (api_configurations attiva). */
  hasLegacyWoo: boolean;
  /** Il gruppo E-commerce del Magazzino si vede (D1 di 15). */
  visible: boolean;
  stores: StoreLabel[];
  byProvider: Record<CommerceProvider, number>;
  /** Variante rossa della card Marketplace per piattaforma. */
  attention: Record<CommerceProvider, 'reauth' | 'suspended' | null>;
}

export const isLiveStatus = (s: StoreStatus): boolean => s !== 'disconnected';

export function computePresence(labels: readonly StoreLabel[], legacyWoo: boolean): CommercePresence {
  const stores = labels.filter((l) => isLiveStatus(l.status));
  const byProvider: Record<CommerceProvider, number> = { shopify: 0, woocommerce: 0 };
  const attention: CommercePresence['attention'] = { shopify: null, woocommerce: null };
  for (const s of stores) {
    byProvider[s.provider] = (byProvider[s.provider] ?? 0) + 1;
    if (s.status === 'reauth_required') attention[s.provider] = 'reauth';
    else if (s.status === 'suspended_billing' && attention[s.provider] === null) attention[s.provider] = 'suspended';
  }
  const hasCommerce = stores.length > 0;
  return { hasCommerce, hasLegacyWoo: legacyWoo, visible: hasCommerce || legacyWoo, stores, byProvider, attention };
}

export interface OverviewStore {
  id: string;
  provider: CommerceProvider;
  shop_domain: string;
  display_name: string | null;
  status: StoreStatus;
  sync_state: unknown;
  updated_at: string | null;
  last_synced_at: string | null;
}

export type BannerKind = 'reauth' | 'suspended' | 'auto_paused' | 'held' | 'errors' | 'conflicts';
export type BannerAction = 'reconnect' | 'open_store' | 'decide' | 'see_errors' | 'see_conflicts';

export interface OverviewBanner {
  kind: BannerKind;
  tone: 'error' | 'warning' | 'info';
  storeId: string;
  text: string;
  action: BannerAction;
  actionLabel: string;
}

const RANK: Record<BannerKind, number> = { reauth: 0, suspended: 1, auto_paused: 2, held: 3, errors: 4, conflicts: 5 };
const n = (x: number, one: string, many: string) => `${formatCountIt(x)} ${x === 1 ? one : many}`;

/** Un banner per problema, in ordine di gravita' (08 sez. 6 punto 1). */
export function overviewBanners(stores: readonly OverviewStore[]): OverviewBanner[] {
  const out: OverviewBanner[] = [];
  for (const s of stores) {
    if (!isLiveStatus(s.status)) continue;
    const name = storeDisplayName(s);
    const platform = PROVIDER_LABEL[s.provider];
    const st = parseSyncState(s.sync_state);
    if (s.status === 'reauth_required') {
      out.push({ kind: 'reauth', tone: 'error', storeId: s.id, action: 'reconnect', actionLabel: 'Ricollega',
        text: `${name} va ricollegato: ${reauthReason(s.sync_state, platform)}. Finché non lo ricolleghi, ordini e giacenze non si aggiornano.` });
      continue;
    }
    if (s.status === 'suspended_billing') {
      out.push({ kind: 'suspended', tone: 'error', storeId: s.id, action: 'open_store', actionLabel: 'Vedi il negozio',
        text: `${name} è sospeso: l'addon Magazzino + E-commerce non è attivo e gli aggiornamenti sono fermi.` });
      continue;
    }
    if (s.status === 'paused' && st.pause && st.pause.reason !== 'manual') {
      out.push({ kind: 'auto_paused', tone: 'error', storeId: s.id, action: 'open_store', actionLabel: 'Vedi il negozio',
        text: `${name} è in pausa: abbiamo fermato gli aggiornamenti per non fare danni.` });
      continue;
    }
    const p = storeProblems(st);
    if (p.held > 0) {
      out.push({ kind: 'held', tone: 'warning', storeId: s.id, action: 'decide', actionLabel: 'Decidi',
        text: `${name} è fermo per un controllo: un aggiornamento aspetta una decisione.` });
    }
    if (p.errors > 0) {
      out.push({ kind: 'errors', tone: 'warning', storeId: s.id, action: 'see_errors', actionLabel: 'Vedi errori',
        text: `Su ${name} ${p.errors === 1 ? 'c\'è' : 'ci sono'} ${n(p.errors, 'elemento non riuscito', 'elementi non riusciti')} dopo i tentativi.` });
    }
    if (p.conflicts > 0) {
      out.push({ kind: 'conflicts', tone: 'info', storeId: s.id, action: 'see_conflicts', actionLabel: 'Vedi conflitti',
        text: `Su ${name} ${p.conflicts === 1 ? 'c\'è' : 'ci sono'} ${n(p.conflicts, 'conflitto', 'conflitti')} da decidere: Flusia e il negozio hanno cambiato gli stessi dati.` });
    }
  }
  return out.sort((a, b) => RANK[a.kind] - RANK[b.kind]);
}

/** "Tutto in ordine: 2 negozi attivi, ultimo aggiornamento 2 min fa." (null se nessun negozio attivo). */
export function overviewLine(stores: readonly OverviewStore[], now: number = Date.now()): string | null {
  const active = stores.filter((s) => s.status === 'connected');
  if (active.length === 0) return null;
  let last: string | null = null;
  for (const s of active) {
    const t = parseSyncState(s.sync_state).updatedAt ?? s.last_synced_at ?? s.updated_at;
    if (t && (!last || Date.parse(t) > Date.parse(last))) last = t;
  }
  const label = active.length === 1 ? '1 negozio attivo' : `${formatCountIt(active.length)} negozi attivi`;
  return `Tutto in ordine: ${label}, ultimo aggiornamento ${relativeTimeIt(last, now)}.`;
}

export interface MenuSignals {
  conflicts: number;
  attention: 'none' | 'amber' | 'red';
}

const BLOCKING: ReadonlySet<StoreStatus> = new Set(['reauth_required', 'suspended_billing']);

/** Numero dei conflitti e pallino della Panoramica (rosso L4, ambra L3). */
export function menuSignals(stores: readonly OverviewStore[]): MenuSignals {
  let conflicts = 0;
  let attention: MenuSignals['attention'] = 'none';
  for (const s of stores) {
    if (!isLiveStatus(s.status)) continue;
    const st = parseSyncState(s.sync_state);
    const p = storeProblems(st);
    conflicts += p.conflicts;
    const red = BLOCKING.has(s.status) || (s.status === 'paused' && !!st.pause && st.pause.reason !== 'manual');
    if (red) attention = 'red';
    else if (attention !== 'red' && (p.held > 0 || s.status === 'awaiting_confirmation')) attention = 'amber';
  }
  return { conflicts, attention };
}
